import React from 'react'

import { FormattedMessage } from 'react-intl'

import Theme from '@theme'
import {
  ChartArea,
  EmptyCaption,
  Marker,
  SparklineHeader,
  SparklineLabel,
  SparklineRoot,
  SparklineSvg,
  SparklineValue,
  XAxisLabel,
  XAxisRow,
} from './Sparkline.styled'

interface IPoint {
  label: string
  value: number | null
}

interface IProps {
  labelKey: string
  points: IPoint[]
  colour?: string
  latestValue?: string
}

const VIEW = 100
const PAD_X = 5
const PAD_Y = 10
const PLOT_W = VIEW - PAD_X * 2
const USABLE_H = VIEW - PAD_Y * 2
const BOTTOM_Y = VIEW - PAD_Y
const CENTER_Y = VIEW / 2
// Dotted stroke shared by the empty-state baseline and the partial-null bridges.
const DASH = '0.5 4'

const fmt = (n: number): number => Math.round(n * 100) / 100
const xAt = (i: number, n: number): number => (n <= 1 ? VIEW / 2 : PAD_X + PLOT_W * (i / (n - 1)))
const toPath = (pts: Array<{ x: number; y: number }>): string =>
  pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${fmt(p.x)} ${fmt(p.y)}`).join(' ')

const Sparkline: React.FC<IProps> = ({
  labelKey,
  points,
  colour = Theme.successColor,
  latestValue,
}) => {
  const n = points.length
  if (n === 0) return null
  const known = points.filter((p): p is { label: string; value: number } => p.value != null)
  const hasData = known.length > 0

  const renderHeader = (value?: string) => (
    <SparklineHeader>
      <SparklineLabel>
        <FormattedMessage id={labelKey} />
      </SparklineLabel>
      {value != null && (
        <SparklineValue data-testid="sparkline-latest-value">{value}</SparklineValue>
      )}
    </SparklineHeader>
  )

  const axis = (
    <XAxisRow>
      {points.map((p, i) => (
        <XAxisLabel
          key={`${p.label}-${i}`}
          $dimmed={p.value == null}
          style={{ left: `${fmt(xAt(i, n))}%` }}
        >
          {p.label}
        </XAxisLabel>
      ))}
    </XAxisRow>
  )

  if (!hasData) {
    return (
      <SparklineRoot>
        {renderHeader(latestValue ?? '—')}
        <ChartArea>
          <SparklineSvg viewBox={`0 0 ${VIEW} ${VIEW}`} preserveAspectRatio="none">
            <line
              x1={PAD_X}
              y1={CENTER_Y}
              x2={VIEW - PAD_X}
              y2={CENTER_Y}
              stroke={Theme.grey400Color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray={DASH}
              vectorEffect="non-scaling-stroke"
            />
          </SparklineSvg>
          <EmptyCaption data-testid="sparkline-empty">
            <FormattedMessage id="creatorSearch.drawer.chart.noData" />
          </EmptyCaption>
        </ChartArea>
        {axis}
      </SparklineRoot>
    )
  }

  const values = known.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const flat = max === min
  const range = max - min || 1
  const yAt = (value: number): number => {
    // No range to scale against: all-zero pins to the bottom (reads as "nothing"),
    // a steady non-zero value centers (reads as "flat, but real").
    if (flat) return max === 0 ? BOTTOM_Y : CENTER_Y
    return PAD_Y + (1 - (value - min) / range) * USABLE_H
  }

  // y is null for months with no data; that's the single source of truth for "missing".
  const coords = points.map((p, i) => ({
    x: xAt(i, n),
    y: p.value == null ? null : yAt(p.value),
  }))

  const solidPaths: string[] = []
  let run: Array<{ x: number; y: number }> = []
  coords.forEach((c) => {
    if (c.y != null) run.push({ x: c.x, y: c.y })
    else {
      if (run.length > 1) solidPaths.push(toPath(run))
      run = []
    }
  })
  if (run.length > 1) solidPaths.push(toPath(run))

  // Index of the nearest known month from i in direction dir, skipping over any
  // nulls in between; -1 if none. A solid line (adjacent) or a dotted bridge
  // (across a gap) connects to this point, so it underpins both the bridge spans
  // and the "stands completely alone" test below.
  const nextKnownIndex = (i: number, dir: -1 | 1): number => {
    let j = i + dir
    while (j >= 0 && j < n && coords[j].y == null) j += dir
    return j >= 0 && j < n && coords[j].y != null ? j : -1
  }
  const reachKnown = (i: number, dir: -1 | 1): boolean => nextKnownIndex(i, dir) !== -1

  // Dotted bridges span only *interior* gaps (a known month on both sides);
  // a leading/trailing gap has nowhere to bridge to.
  const bridges: string[] = []
  for (let i = 0; i < n; i++) {
    if (coords[i].y != null) continue
    const a = nextKnownIndex(i, -1)
    const b = nextKnownIndex(i, 1)
    const pa = a === -1 ? null : coords[a]
    const pb = b === -1 ? null : coords[b]
    if (pa?.y != null && pb?.y != null) {
      bridges.push(`M ${fmt(pa.x)} ${fmt(pa.y)} L ${fmt(pb.x)} ${fmt(pb.y)}`)
    }
    // Jump past the rest of this null run (b is the next known, or -1 at the edge).
    i = (b === -1 ? n : b) - 1
  }

  // A known month abutting a missing one terminates a segment, so it gets a
  // hollow boundary circle — covering interior gaps AND leading/trailing nulls
  // (e.g. avg likes starting null, followers ending null) the same way. A known
  // month connected to nothing (lone value between gaps, or a single-month
  // series) stands alone and reads as one filled dot instead.
  const markers: Array<{ x: number; y: number }> = []
  const dots: Array<{ x: number; y: number }> = []
  coords.forEach((c, i) => {
    if (c.y == null) return
    if (!reachKnown(i, -1) && !reachKnown(i, 1)) {
      dots.push({ x: c.x, y: c.y })
      return
    }
    const gapLeft = i > 0 && coords[i - 1].y == null
    const gapRight = i < n - 1 && coords[i + 1].y == null
    if (gapLeft || gapRight) markers.push({ x: c.x, y: c.y })
  })

  return (
    <SparklineRoot>
      {renderHeader(latestValue)}
      <ChartArea>
        <SparklineSvg viewBox={`0 0 ${VIEW} ${VIEW}`} preserveAspectRatio="none">
          {bridges.map((d, i) => (
            <path
              key={`bridge-${i}`}
              data-testid="sparkline-bridge"
              d={d}
              fill="none"
              stroke={Theme.grey400Color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeDasharray={DASH}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {solidPaths.map((d, i) => (
            <path
              key={`solid-${i}`}
              data-testid="sparkline-path"
              d={d}
              fill="none"
              stroke={colour}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </SparklineSvg>
        {markers.map((m, i) => (
          <Marker
            key={`marker-${i}`}
            data-testid="sparkline-marker"
            $colour={colour}
            style={{ left: `${fmt(m.x)}%`, top: `${fmt(m.y)}%` }}
          />
        ))}
        {dots.map((d, i) => (
          <Marker
            key={`dot-${i}`}
            data-testid="sparkline-dot"
            $colour={colour}
            $filled
            style={{ left: `${fmt(d.x)}%`, top: `${fmt(d.y)}%` }}
          />
        ))}
      </ChartArea>
      {axis}
    </SparklineRoot>
  )
}

export { Sparkline }
export default Sparkline
